<template>
    <AccountPageContainer>
        <SearchSection>
            <label style="margin-right: 15px;">고객명</label>
            <SearchInput type="text" v-model="virtualSearch" @keyup.enter="handleSearch"
                placeholder="고객명 또는 전화번호 입력하세요">
            </SearchInput>
            <SearchButton @click="handleSearch">
                <Magnify />
            </SearchButton>

            <div class="virtual-date-controls" style="display: inline-flex; align-items: center; margin-right: 15px;">
                <Datepicker v-model:value="virtualSDate" placeholder="시작일" style="width: 130px;" :lang="ko" />
                ~
                <Datepicker v-model:value="virtualEDate" placeholder="종료일" style="width: 130px;" :lang="ko" />
                <AccountButton @click="loadVirtualAccounts" style="margin-left: 5px;">날짜조회</AccountButton>
            </div>
            <div class="virtual-date-buttons" style="display: inline-flex; align-items: center; margin-right: 15px;">
                <AccountDateButton @click="setVirtualDateRange('today')">당일</AccountDateButton>
                <AccountDateButton @click="setVirtualDateRange('yesterday')">전일</AccountDateButton>
                <AccountDateButton @click="setVirtualDateRange('thisMonth')">당월</AccountDateButton>
                <AccountDateButton @click="setVirtualDateRange('lastMonth')">전월</AccountDateButton>
                <AccountDateButton @click="setVirtualDateRange('all')">전체</AccountDateButton>
            </div>

            <!-- * 입금여부 필터 -->
            <StatusFilterContainer>
                <FilterLabel>입금여부</FilterLabel>
                <FilterGroup>
                    <FilterBadge :active="virtualStatus === '전체'" @click="virtualStatus = '전체'; handleStatusChange()">
                        전체
                    </FilterBadge>
                    <FilterBadge :active="virtualStatus === '납부'" @click="virtualStatus = '납부'; handleStatusChange()">
                        납부
                    </FilterBadge>
                    <FilterBadge :active="virtualStatus === '미납'" @click="virtualStatus = '미납'; handleStatusChange()">
                        미납
                    </FilterBadge>
                    <FilterBadge :active="virtualStatus === '등록실패'"
                        @click="virtualStatus = '등록실패'; handleStatusChange()">
                        등록실패
                    </FilterBadge>
                </FilterGroup>

                <FilterLabel>항목명</FilterLabel>
                <FilterGroup>
                    <FilterBadge :active="paymentType === '전체'" @click="paymentType = '전체'; handlePaymentTypeChange()">
                        전체
                    </FilterBadge>
                    <FilterBadge :active="paymentType === '급여'" @click="paymentType = '급여'; handlePaymentTypeChange()">
                        급여
                    </FilterBadge>
                    <FilterBadge :active="paymentType === '비급여'"
                        @click="paymentType = '비급여'; handlePaymentTypeChange()">
                        비급여
                    </FilterBadge>
                </FilterGroup>
            </StatusFilterContainer>
        </SearchSection>

        <ResultCount>
            <ButtonGroup>
                <VirtualAccountButton @click="openAccountSubModal">
                    <CreditCardPlus :style="{ paddingTop: '3px', paddingRight: '5px' }" />가상계좌 발송
                </VirtualAccountButton>
                <VirtualAccountButton @click="openExcelDownload">
                    <MicrosoftExcel :style="{ paddingTop: '3px', paddingRight: '5px' }" />가상계좌 엑셀 다운로드
                </VirtualAccountButton>
                <VirtualAccountButton @click="openExcelUpload">
                    <AccountCheck :style="{ paddingTop: '3px', paddingRight: '5px' }" />가상계좌 채번 등록
                </VirtualAccountButton>
            </ButtonGroup>

            <div class="stat-item">
                사용가능계좌<span class="value success" :class="{ 'blurred': isTestUser }">{{ availableAccount }}</span>개
            </div>
            <div class="stat-item" @click="openAccountUseModal">
                사용중계좌<span class="value" :class="{ 'blurred': isTestUser }">{{ usedAccount }}</span>개
            </div>
            <div class="stat-item">
                납부<span class="value success" :class="{ 'blurred': isTestUser }">{{
                    virtualAccountObj.success_amt.toLocaleString() }}</span>원
            </div>
            <div class="stat-item">
                미납<span class="value warning" :class="{ 'blurred': isTestUser }">{{
                    virtualAccountObj.danger_amt.toLocaleString() }}</span>원
            </div>
            <div class="stat-item">
                등록실패<span class="value warning" :class="{ 'blurred': isTestUser }">{{
                    virtualAccountObj.error_amt.toLocaleString() }}</span>원
            </div>
        </ResultCount>

        <TableContainer>
            <!-- 로딩 스피너 -->
            <LoadingOverlay v-if="isLoadingHistory">
                <LoadingSpinner></LoadingSpinner>
                <LoadingText>가상계좌 목록을 불러오는 중입니다...</LoadingText>
            </LoadingOverlay>

            <template v-if="totalItems > 0 && !isLoadingHistory">
                <table>
                    <thead>
                        <tr>
                            <th>순번</th>
                            <th>등록일</th>
                            <th>고객명</th>
                            <th>인정번호</th>
                            <th>연락처</th>
                            <th>가상계좌<br>은행</th>
                            <th>가상계좌번호</th>
                            <th>입금가능기간</th>
                            <th>항목명</th>
                            <th>입금금액</th>
                            <th>입금여부<br>(약 15분 소요)</th>
                            <th>비고</th>
                            <th>복사</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in pagedData" :key="index" @click="openAccountDetailModal(index)"
                            :class="{ 'blurred': isTestUser }">
                            <td>{{ totalItems - ((currentPage - 1) * itemsPerPage + index) }}</td>
                            <td>{{ item.PAYMASDT }}</td>
                            <td>
                                {{ item.CUSNM }}
                                <br v-if="item.EMAIL !== null" />
                                <OrderViewButton v-if="item.EMAIL !== null" @click.stop="$swal.fire('개발중입니다')">
                                    주문서보기
                                </OrderViewButton>
                            </td>
                            <td>{{ item.CONTENT1 || "없음" }}</td>
                            <td>{{ formatMobile(item.MOBILENO) }}</td>
                            <td>농협</td>
                            <td>{{ item.VANO }}</td>
                            <td>{{ formatDateRange(item.RCPSTARTDT, item.RCPENDDT) }}</td>
                            <td>{{ item.PAYITEMNM }}</td>
                            <td>{{ Number(item.PAYITEMAMT).toLocaleString() }}원</td>
                            <td :class="getStatusClass(item)" v-html="getResultMessage(item)" style="font-weight: 600;">
                            </td>
                            <td>{{ item.txt || '' }}</td>
                            <td>
                                <AccountCopyBtn @click.stop="copyAccountData(item)">
                                    <ContentCopy :size="22" />
                                </AccountCopyBtn>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- !페이지네이션 -->
                <PaginationContainer>
                    <PaginationButton :disabled="currentPage === 1" @click="changePage(1)">
                        <ChevronDoubleLeft :size="17" class="chevron-icon" />
                    </PaginationButton>
                    <PaginationButton :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
                        <ChevronLeft :size="17" class="chevron-icon" />
                    </PaginationButton>
                    <PaginationNumbers>
                        <PaginationNumber v-for="page in displayedPageNumbers" :key="page"
                            :class="{ active: currentPage === page }" @click="changePage(page)">
                            {{ page }}
                        </PaginationNumber>
                    </PaginationNumbers>
                    <PaginationButton :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
                        <ChevronRight :size="17" class="chevron-icon" />
                    </PaginationButton>
                    <PaginationButton :disabled="currentPage === totalPages" @click="changePage(totalPages)">
                        <ChevronDoubleRight :size="17" class="chevron-icon" />
                    </PaginationButton>
                </PaginationContainer>
            </template>
        </TableContainer>
    </AccountPageContainer>

    <!-- !가상계좌 발송 모달 -->
    <ModalPage v-model:show="showModal_obj.virtual_up" :width="modal_width.virtual_up"
        :height="modal_height.virtual_up">
        <AccountSendModal @close="showModal_obj.virtual_up = false" @success="handleVirtualAccountSuccess" />
    </ModalPage>

    <!-- !가상계좌 상세 모달 -->
    <ModalPage v-model:show="showModal_obj.virtual_detail" :width="modal_width.virtual_detail"
        :height="modal_height.virtual_detail">
        <AccountDetailModal :initialDetail="seletedDetail" @close="showModal_obj.virtual_detail = false"
            @deleted="handleDetailDeleted" @success="handleVirtualAccountSuccess" />
    </ModalPage>

    <!-- ! 사용중계좌 모달 -->
    <ModalPage v-model:show="showModal_obj.virtual_use" :width="modal_width.virtual_use"
        :height="modal_height.virtual_use">
        <AccountUseModal :details="virtualAccountObj.use_list" @closeModal="showModal_obj.virtual_use = false" />
    </ModalPage>
</template>

<script>
import { reactive } from 'vue';

import ModalPage from '@/components/layout/ModalPage.vue';
import * as AccountCss from '@/assets/styles/account/AccountCss.js';
import AccountSendModal from '@/components/account/modal/AccountSendModal.vue';
import AccountDetailModal from '@/components/account/modal/AccountDetailModal.vue';
import AccountUseModal from '@/components/account/modal/AccountUseModal.vue';

import Magnify from 'vue-material-design-icons/Magnify.vue';
import CreditCardPlus from 'vue-material-design-icons/CreditCardPlus.vue';
import Datepicker from 'vue-datepicker-next';
import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import ChevronDoubleLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import ChevronDoubleRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import MicrosoftExcel from 'vue-material-design-icons/MicrosoftExcel.vue';
import ContentCopy from 'vue-material-design-icons/ContentCopy.vue';
import AccountCheck from 'vue-material-design-icons/AccountCheck.vue';

export default {
    name: 'AccountPage',

    components: {
        ...AccountCss,
        ModalPage,
        AccountSendModal,
        AccountDetailModal,
        AccountUseModal,
        Magnify, CreditCardPlus, Datepicker, ChevronLeft, ChevronRight, ChevronDoubleLeft, ChevronDoubleRight, MicrosoftExcel, ContentCopy, AccountCheck
    },

    setup() {
        const showModal_obj = reactive({
            virtual_up: false,
            virtual_detail: false,
            virtual_use: false,
        });

        const modal_width = {
            virtual_up: '25%',
            virtual_detail: '25%',
            virtual_use: '25%'
        };

        const modal_height = {
            virtual_up: '75%',
            virtual_detail: '70%',
            virtual_use: '70%'
        };

        const ko = {
            formatLocale: {
                months: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
                monthsShort: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
                weekdays: ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
                weekdaysShort: ['일', '월', '화', '수', '목', '금', '토'],
                weekdaysMin: ['일', '월', '화', '수', '목', '금', '토'],
                firstDayOfWeek: 0,
                firstWeekContainsDate: 1,
                meridiem: (h) => (h < 12 ? '오전' : '오후'),
                meridiemParse: /오전|오후/,
                isPM: (input) => input === '오후'
            },
            yearFormat: 'YYYY년',
            monthFormat: 'MMM',
            monthBeforeYear: false
        };

        return {
            ko,
            showModal_obj,
            modal_width,
            modal_height,
        }
    },

    data() {
        return {
            currentPage: 1,
            itemsPerPage: 10,
            totalItems: 0,
            isLoadingHistory: false,
            // 가상계좌 관련 데이터
            virtualSearch: "",
            virtualSDate: new Date(1999, 0, 1),
            virtualEDate: new Date(2099, 0, 1),
            virtualStatus: "전체",
            paymentType: "전체",
            availableAccount: 0,
            usedAccount: 0,
            virtualAccountObj: {
                data: [],
                success_amt: 0,
                danger_amt: 0,
                error_amt: 0,
                use_list: [],
                notuse_list: [],
                search_data: []
            },
        }
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        // 선택된 virtualStatus에 따라 전체 목록을 필터링
        filteredVirtualAccountData() {
            // 1. 날짜를 기준으로 내림차순 정렬하여 최신 데이터가 앞에 오도록 함
            let sortedData = [...this.virtualAccountObj.data].sort((a, b) => {
                // PAYMASDT를 비교하여 최신 날짜가 앞으로 오도록 정렬
                return b.PAYMASDT.localeCompare(a.PAYMASDT);
            });

            // 2. 데이터가 15개 이상인 경우에만 고객명별로 첫 번째(최신) 데이터만 유지
            let filteredData = sortedData;
            if (sortedData.length >= 15) {
                const uniqueCustomers = new Set();
                filteredData = sortedData.filter(item => {
                    if (!uniqueCustomers.has(item.CUSNM)) {
                        uniqueCustomers.add(item.CUSNM);
                        return true;
                    }
                    return false;
                });
            }

            // 3. 상태별 필터링 적용
            if (this.virtualStatus === "등록실패") {
                filteredData = filteredData.filter(item => item.RESULTCD !== "D000");
            } else {
                // 그 외에는 등록실패 항목 제거 (RESULTCD !== "D000" 인 항목 제외)
                filteredData = filteredData.filter(item => item.RESULTCD === "D000");

                // 상태 필터 추가
                if (this.virtualStatus === "납부") {
                    filteredData = filteredData.filter(item => item.REGDT !== null);
                } else if (this.virtualStatus === "미납") {
                    filteredData = filteredData.filter(item => !item.REGDT);
                }
            }

            // 4. 급여/비급여 유형 필터링 적용
            if (this.paymentType !== "전체") {
                if (this.paymentType === "급여") {
                    filteredData = filteredData.filter(item => item.EMAIL !== null);
                } else if (this.paymentType === "비급여") {
                    filteredData = filteredData.filter(item => item.EMAIL === null);
                }
            }

            return filteredData;
        },

        // 페이지네이션을 적용할 목록
        pagedData() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredVirtualAccountData.slice(start, start + this.itemsPerPage);
        },

        // 총 페이지 수 (필터링된 데이터 기준)
        totalPages() {
            return Math.ceil(this.filteredVirtualAccountData.length / this.itemsPerPage);
        },

        currentPageGroup() {
            return Math.ceil(this.currentPage / 10);
        },

        displayedPageNumbers() {
            const startPage = (this.currentPageGroup - 1) * 10 + 1;
            const endPage = Math.min(startPage + 9, this.totalPages);

            return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
        }
    },

    mounted() {
        window.virtual_account_vm = this;
    },

    methods: {
        // 가상계좌 채번 등록
        async openExcelUpload() {
            try {
                // 파일 입력 요소 생성
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.xlsx,.xls';
                input.style.display = 'none';

                input.onchange = async (event) => {
                    const file = event.target.files[0];
                    if (!file) {
                        return;
                    }

                    // 파일 확장자 검증
                    const fileName = file.name.toLowerCase();
                    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
                        this.$swal.fire({
                            title: '파일 형식 오류',
                            text: '엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.',
                            icon: 'error',
                            confirmButtonText: '확인'
                        });
                        return;
                    }

                    // 파일 크기 검증 (30MB 제한)
                    if (file.size > 30 * 1024 * 1024) {
                        this.$swal.fire({
                            title: '파일 크기 초과',
                            text: '파일 크기는 30MB를 초과할 수 없습니다.',
                            icon: 'error',
                            confirmButtonText: '확인'
                        });
                        return;
                    }

                    try {
                        // 로딩 표시
                        const loading = this.$swal.fire({
                            title: '엑셀 파일 업로드 중...',
                            text: '가상계좌 채번 등록을 진행 중입니다.',
                            allowOutsideClick: false,
                            didOpen: () => {
                                this.$swal.showLoading();
                            }
                        });

                        // FormData 생성
                        const formData = new FormData();
                        formData.append('excelFile', file);
                        formData.append('company', this.$globalState.company);

                        // 백엔드로 업로드 요청
                        // 일렉트론 환경에서는 원본 axios를 직접 사용하여 FormData가 그대로 전송되도록 함
                        let response;
                        if (this.$env.isElectron && window.electronAPI) {
                            // 일렉트론 환경: 원본 axios 사용 (FormData 형식 유지)
                            const axiosInstance = (await import('axios')).default;
                            response = await axiosInstance.post(
                                `${this.$apiUrls.hanqmedi}/virtualaccount/upload`,
                                formData,
                                {
                                    headers: {
                                        'Content-Type': 'multipart/form-data',
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    },
                                    timeout: 60000 // 60초 타임아웃
                                }
                            );
                        } else {
                            // 브라우저 환경: 기존 방식 사용
                            response = await this.$axios.post(
                                `${this.$apiUrls.hanqmedi}/virtualaccount/upload`,
                                formData,
                                {
                                    headers: {
                                        'Content-Type': 'multipart/form-data',
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    },
                                    timeout: 60000 // 60초 타임아웃
                                }
                            );
                        }

                        loading.close();

                        if (response.data.code === 200) {
                            const result = response.data.data;

                            // 성공 메시지 표시
                            let messageHtml = `
                                <div style="text-align: left;">
                                    <p><strong>가상계좌 채번 등록이 완료되었습니다.</strong></p>
                                    <p>총 ${result.total}건 중</p>
                                    <p style="color: #28a745;">✓ 성공: ${result.success}건</p>
                                    <p style="color: #dc3545;">✗ 실패: ${result.failed}건</p>
                            `;

                            // 실패한 항목이 있는 경우 상세 정보 표시
                            if (result.errors && result.errors.length > 0) {
                                messageHtml += `
                                    <hr style="margin: 15px 0;">
                                    <p><strong>실패 상세:</strong></p>
                                    <div style="max-height: 200px; overflow-y: auto; background: #f8f9fa; padding: 10px; border-radius: 4px;">
                                        <table style="width: 100%; font-size: 12px;">
                                            <thead>
                                                <tr style="background: #e9ecef;">
                                                    <th style="padding: 5px; text-align: left;">행</th>
                                                    <th style="padding: 5px; text-align: left;">가상계좌</th>
                                                    <th style="padding: 5px; text-align: left;">오류</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                `;

                                result.errors.forEach(error => {
                                    messageHtml += `
                                <tr>
                                    <td style="padding: 5px;">${error.row}</td>
                                    <td style="padding: 5px;">${error.가상계좌 || '-'}</td>
                                    <td style="padding: 5px; color: #dc3545;">${error.error}</td>
                                </tr>
                            `;
                                });

                                messageHtml += `
                                    </tbody>
                                </table>
                            </div>
                        `;
                            }

                            messageHtml += `</div>`;

                            await this.$swal.fire({
                                title: '등록 완료',
                                html: messageHtml,
                                icon: 'success',
                                confirmButtonText: '확인',
                                width: '600px'
                            });

                            // 가상계좌 목록 새로고침
                            this.loadVirtualAccounts();
                        } else {
                            throw new Error(response.data.message || '가상계좌 채번 등록에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('엑셀 업로드 오류:', error);

                        let errorMessage = '가상계좌 채번 등록 중 오류가 발생했습니다.';
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.message) {
                            errorMessage = error.message;
                        }

                        this.$swal.fire({
                            title: '업로드 실패',
                            text: errorMessage,
                            icon: 'error',
                            confirmButtonText: '확인'
                        });
                    }
                };

                // 파일 선택 다이얼로그 열기
                document.body.appendChild(input);
                input.click();
                document.body.removeChild(input);

            } catch (error) {
                console.error('파일 선택 오류:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '파일 선택 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 가상계좌 발송 성공 처리
        handleVirtualAccountSuccess() {
            // 가상계좌 목록 새로고침
            this.loadVirtualAccounts();

            // 상태 저장
            this.saveState();
        },

        handleDetailDeleted() {
            this.table_search();
            this.saveState();
        },

        // 가상계좌 복사 =====================================================================
        copyAccountData(item) {
            console.log('item', item);
            if (!item) return;

            // 입금 여부 계산
            const depositStatus = item.REGDT !== null ? "납부" : "미납";

            // 가상계좌은행 코드 변환
            const bankCode = {
                "011": "농협",
                "012": "하나은행",
                "013": "신한은행",
                "014": "우리은행",
            }

            // 입금가능기간
            const depositPeriod = `${item.RCPSTARTDT} ~ ${item.RCPENDDT}`;

            // 데이터 포맷
            const dataToCopy = `
                등록일: ${item.PAYMASDT || ""}
                고객명: ${item.CUSNM || ""}
                인정번호: ${item.CONTENT1 || ""}
                연락처: ${item.MOBILENO || ""}
                가상계좌은행: ${bankCode[item.VANOBNKCD] || ""}
                가상계좌번호: ${item.VANO || ""}
                입금가능기간: ${depositPeriod || ""}
                항목명: ${item.PAYITEMNM || ""}
                입금금액: ${item.PAYITEMAMT ? Number(item.PAYITEMAMT).toLocaleString('ko-KR') + "원" : ""}
                입금여부: ${depositStatus}
                비고: ${item.txt || ""}
            `.trim();

            // 클립보드 복사
            navigator.clipboard.writeText(dataToCopy).then(() => {
                this.$swal.fire({
                    title: '복사 완료',
                    text: '가상계좌 정보가 복사되었습니다.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            }).catch(err => {
                this.$swal.fire({
                    title: '복사 실패',
                    text: '가상계좌 정보 복사에 실패했습니다.',
                    icon: 'error',
                    timer: 2000,
                    showConfirmButton: false
                });
            });
        },

        async openExcelDownload() {
            try {
                const result = await this.$swal.fire({
                    title: '엑셀 다운로드',
                    html: `
                    <div style="text-align: center; margin-bottom: 10px; padding: 0 10px; box-sizing: border-box;">
                        <label style="display:block; margin-bottom: 5px; font-size: 16px;">다운로드 범위:</label>
                        <select id="downloadRange" class="swal2-select" style="width: 100%; max-width: 300px; font-size: 16px; padding: 6px; box-sizing: border-box;">
                        <option value="current">현재 페이지</option>
                        <option value="all">전체 페이지</option>
                        </select>
                    </div>
                    `,
                    icon: 'info',
                    showCancelButton: true,
                    confirmButtonText: '다운로드',
                    cancelButtonText: '취소',
                    width: 400
                });

                if (!result.isConfirmed) return;

                // 사용자가 선택한 조건 읽기
                const downloadRange = document.getElementById('downloadRange').value;

                // 전체 필터된 데이터 기준으로 다시 필터링
                let filteredData = [...this.filteredVirtualAccountData];

                // 범위에 따라 데이터 제한
                let dataToExport = [];
                if (downloadRange === 'current') {
                    const start = (this.currentPage - 1) * this.itemsPerPage;
                    const end = start + this.itemsPerPage;
                    dataToExport = filteredData.slice(start, end);
                } else {
                    dataToExport = filteredData;
                }

                if (dataToExport.length === 0) {
                    this.$swal.fire({
                        title: '데이터 없음',
                        text: '선택한 조건에 해당하는 데이터가 없습니다.',
                        icon: 'info',
                        confirmButtonText: '확인'
                    });
                    return;
                }

                // 파일명 생성
                const today = new Date();
                const fileName = `가상계좌목록_${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}_${String(today.getHours()).padStart(2, '0')}${String(today.getMinutes()).padStart(2, '0')}.xlsx`;

                // 데이터 정제 함수 (Vue reactive 객체 → 일반 객체)
                const sanitizeData = (data) => {
                    return data.map(item => {
                        const sanitized = {};
                        for (const key in item) {
                            if (item.hasOwnProperty(key) && typeof item[key] !== 'function') {
                                sanitized[key] = item[key];
                            }
                        }
                        return sanitized;
                    });
                };

                // 로딩 표시
                const loading = this.$swal.fire({
                    title: '엑셀 파일 생성 중...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                // 일렉트론 환경 우선 처리
                if (this.$env.isElectron && window.electronAPI && window.electronAPI.downloadVirtualExcel) {
                    try {
                        console.log('🔧 일렉트론 환경에서 엑셀 다운로드 시작');

                        // Vue reactive 객체를 일반 객체로 변환
                        const plainDataToExport = sanitizeData(dataToExport);

                        // IPC를 통해 서버 요청 및 엑셀 생성
                        const downloadResult = await window.electronAPI.downloadVirtualExcel(plainDataToExport);

                        if (!downloadResult.success) {
                            loading.close();
                            throw new Error(downloadResult.error || '엑셀 생성 중 오류가 발생했습니다.');
                        }

                        // IPC를 통해 파일 저장
                        const saveResult = await window.electronAPI.saveFile(fileName, downloadResult.base64Data);

                        loading.close();

                        if (saveResult.success) {
                            this.$swal.fire({
                                title: '다운로드 완료',
                                html: `파일이 저장되었습니다<br/><small>${saveResult.filePath}</small>`,
                                icon: 'success',
                                timer: 4000,
                                showConfirmButton: true,
                                confirmButtonText: '확인'
                            });
                            return;
                        } else {
                            throw new Error(saveResult.error || '파일 저장 중 오류가 발생했습니다.');
                        }

                    } catch (electronError) {
                        loading.close();
                        console.error('일렉트론 다운로드 실패:', electronError);

                        // 일렉트론에서 실패한 경우 에러 표시하고 종료 (브라우저 방식으로 대체하지 않음)
                        this.$swal.fire({
                            title: '다운로드 실패',
                            text: `일렉트론 환경에서 다운로드 실패: ${electronError.message}`,
                            icon: 'error',
                            confirmButtonText: '확인'
                        });
                        return;
                    }
                }

                // 브라우저 환경 처리 (일렉트론이 아닌 경우에만)
                try {
                    console.log('🌐 브라우저 환경에서 엑셀 다운로드 시작');

                    const axiosInstance = (await import('axios')).default;
                    const response = await axiosInstance({
                        method: 'post',
                        url: `${this.$apiUrls.hanqmedi}/downloadVirtualExcel`,
                        data: { data: dataToExport },
                        responseType: 'blob',
                        headers: { 'Content-Type': 'application/json' },
                        timeout: 60000
                    });

                    loading.close();

                    if (!response.data || response.data.size === 0) {
                        throw new Error('다운로드된 파일이 비어 있습니다.');
                    }

                    const blob = new Blob([response.data], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });

                    // 브라우저 다운로드
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    setTimeout(() => {
                        window.URL.revokeObjectURL(url);
                    }, 1000);

                    this.$swal.fire({
                        title: '다운로드 완료',
                        text: '엑셀 파일이 다운로드되었습니다.',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });

                } catch (browserError) {
                    loading.close();
                    console.error('브라우저 다운로드 실패:', browserError);
                    throw browserError;
                }

            } catch (error) {
                console.error('엑셀 다운로드 중 오류:', error);
                this.$swal.fire({
                    title: '다운로드 실패',
                    text: error.message || '엑셀 파일 다운로드 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // Blob → Base64 변환 헬퍼
        blobToBase64(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        },

        // 상태 저장 섹션 ================================================================================
        saveState() {
            const state = {
                // 검색 관련 상태
                virtualSearch: this.virtualSearch,
                virtualSDate: this.virtualSDate,
                virtualEDate: this.virtualEDate,
                virtualStatus: this.virtualStatus,
                paymentType: this.paymentType,

                // 페이지네이션 상태
                currentPage: this.currentPage,

                // 모달 상태
                showModal_obj: this.showModal_obj,
                seletedDetail: this.seletedDetail,

                // 마지막 방문 시간 저장
                lastVisit: new Date().getTime()
            };

            localStorage.setItem('accountPageState', JSON.stringify(state));

            // 새로고침 감지를 위한 세션 스토리지 설정
            sessionStorage.setItem('accountPageVisited', 'true');
        },

        // 상태 복원 메서드 추가
        restoreState() {
            const isRefresh = sessionStorage.getItem('accountPageVisited') === 'true';

            // 새로고침인 경우 상태를 복원하지 않고 초기화
            if (isRefresh) {
                console.log('새로고침 감지: 상태 초기화');
                localStorage.removeItem('accountPageState');
                sessionStorage.removeItem('accountPageVisited');
                return;
            }

            const savedState = localStorage.getItem('accountPageState');
            if (!savedState) return;

            try {
                const state = JSON.parse(savedState);

                // 검색 관련 상태 복원
                this.virtualSearch = state.virtualSearch || "";

                // 날짜 복원
                if (state.virtualSDate) this.virtualSDate = new Date(state.virtualSDate);
                if (state.virtualEDate) this.virtualEDate = new Date(state.virtualEDate);

                this.virtualStatus = state.virtualStatus || "전체";
                this.paymentType = state.paymentType || "전체";

                // 페이지네이션 상태 복원
                this.currentPage = state.currentPage || 1;

                // 모달 상태 복원
                if (state.showModal_obj) {
                    Object.assign(this.showModal_obj, state.showModal_obj);
                }

                if (state.seletedDetail) {
                    this.seletedDetail = state.seletedDetail;
                }

            } catch (error) {
                console.error('상태 복원 중 오류 발생:', error);
                localStorage.removeItem('accountPageState');
            }
        },
        // 상태 저장 섹션 ================================================================================

        handleStatusChange() {
            this.currentPage = 1;
            this.loadVirtualAccounts();
            this.saveState();
        },
        handlePaymentTypeChange() {
            this.currentPage = 1;
            this.loadVirtualAccounts();
            this.saveState();
        },
        formatDate(dateObj) {
            if (!(dateObj instanceof Date)) {
                dateObj = new Date(dateObj);
            }
            const year = dateObj.getFullYear();
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
            const day = dateObj.getDate().toString().padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        setVirtualDateRange(range) {
            console.log(`setVirtualDateRange 호출됨: ${range}`);
            const today = new Date();
            let sdate, edate;
            switch (range) {
                case 'today':
                    sdate = new Date(today);
                    edate = new Date(today);
                    break;
                case 'yesterday':
                    sdate = new Date(today);
                    sdate.setDate(today.getDate() - 1);
                    edate = new Date(sdate);
                    break;
                case 'thisMonth':
                    sdate = new Date(today.getFullYear(), today.getMonth(), 1);
                    edate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                    break;
                case 'lastMonth':
                    sdate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    edate = new Date(today.getFullYear(), today.getMonth(), 0);
                    break;
                case 'all':
                    sdate = new Date(1999, 0, 1);
                    edate = new Date(2099, 0, 1);
                    break;
                default:
                    sdate = new Date(today);
                    edate = new Date(today);
            }
            this.virtualSDate = sdate;
            this.virtualEDate = edate;
            this.loadVirtualAccounts();
            this.saveState();
        },

        handleSearch() {
            this.currentPage = 1;
            this.table_search();
            this.saveState();
        },

        openAccountSubModal() {
            this.showModal_obj.virtual_up = true;
            this.saveState();
        },

        openAccountDetailModal(index) {
            this.seletedDetail = this.pagedData[index];
            this.showModal_obj.virtual_detail = true;
            this.saveState();
        },

        openAccountUseModal() {
            this.showModal_obj.virtual_use = true;
            this.saveState();
        },

        handleDetailDeleted() {
            this.table_search();
            this.saveState();
        },

        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.saveState();
            }
        },
        async loadVirtualAccounts() {
            this.isLoadingHistory = true;

            try {
                // 가상계좌 목록 조회
                const response = await this.getlist('');

                if (response.data.code === 200) {
                    this.virtualAccountObj.data = response.data.data || [];
                    this.totalItems = this.filteredVirtualAccountData.length;

                    if (this.virtualAccountObj.data.length > 0) {
                        // 금액 초기화
                        this.virtualAccountObj.success_amt = 0;
                        this.virtualAccountObj.danger_amt = 0;
                        this.virtualAccountObj.error_amt = 0;

                        // 금액 계산
                        this.virtualAccountObj.data.forEach(item => {
                            if (item.RESULTCD !== "D000") { // 등록실패: 결과 코드가 "D000"가 아닌 경우
                                this.virtualAccountObj.error_amt += parseInt(item.PAYITEMAMT);
                            } else if (item.REGDT !== null) { // 납부: 결과 코드가 "D000"이면서 등록일이 있는 경우
                                this.virtualAccountObj.success_amt += parseInt(item.PAYITEMAMT);
                            } else { // 미납: 결과 코드가 "D000"이지만 등록일이 없는 경우
                                this.virtualAccountObj.danger_amt += parseInt(item.PAYITEMAMT);
                            }
                        });
                    } else {
                        this.virtualAccountObj.data = [];
                    }
                } else {
                    console.log(response.data.message);
                    this.virtualAccountObj.data = [];
                    this.totalItems = 0;
                }

                // 사용 가능/사용중 계좌 수 조회
                const accResponse = await this.getacc();
                if (accResponse.data.code === 200) {
                    this.availableAccount = accResponse.data.data.notuse_cnt;
                    this.usedAccount = accResponse.data.data.use_cnt;
                }
                this.saveState();
            } catch (error) {
                console.error('가상계좌 목록 로드 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '가상계좌 목록을 불러오는데 실패했습니다.',
                    icon: 'error'
                });
            } finally {
                this.isLoadingHistory = false;
            }
        },

        formatMobile(mobile) {
            return mobile.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
        },
        formatDateRange(start, end) {
            const formatDate = (dateStr) => {
                return dateStr.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
            }
            return formatDate(start) + " ~ " + formatDate(end);
        },
        getStatusClass(item) {
            if (!item) return '';

            if (item.RESULTCD === null) {
                return 'pending';  // 등록대기
            } else if (item.RESULTCD !== "D000") {
                return 'error';    // 등록실패
            } else {
                return item.REGDT !== null ? 'pay' : 'non-pay';  // 납부/미납
            }
        },
        getResultMessage(item) {
            if (item.RESULTCD === null) {
                return '등록대기';
            } else if (item.RESULTCD !== "D000") {
                return `등록실패[${item.RESULTCD}]<br/>${item.RESULTMSG}`;
            } else {
                if (item.REGDT !== null) {
                    return `납부<br/> ${item.PAYDAY.replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/g, '$1-$2-$3 $4:$5:$6')}`;
                } else {
                    return '미납';
                }
            }
        },

        // *--------------------------------가상계좌 기능--------------------------------
        async table_search() {
            this.isLoadingHistory = true; // 로딩 시작
            const search_data = this.virtualSearch;

            try {
                const response = await this.getlist(search_data);

                // 검색어가 있고 검색 결과가 없는 경우에만 SweetAlert 메시지 표시
                if (search_data && search_data.trim() !== '' &&
                    (!response.data.data || response.data.data.length === 0)) {
                    this.$swal.fire({
                        title: '검색 결과 없음',
                        text: `"${search_data}" 검색 결과를 찾을 수 없습니다.`,
                        icon: 'info',
                        confirmButtonText: '확인'
                    });
                }

                await this.row_init(search_data);
                this.getacc().then(response => {
                    if (response.data.code === 200) {
                        this.availableAccount = response.data.data.notuse_cnt;
                        this.usedAccount = response.data.data.use_cnt;
                    }
                    this.saveState();
                });
            } finally {
                this.isLoadingHistory = false;
            }
        },
        async getlist(search_data) {
            const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/virtualaccount/getlist`, {
                search: search_data,
                sdate: this.virtualSDate ? this.formatDate(this.virtualSDate) : "1999-01-01",
                edate: this.virtualEDate ? this.formatDate(this.virtualEDate) : "2099-01-01",
                status: this.virtualStatus || "전체",
                paymentType: this.paymentType || "전체",
                company: this.$globalState.company
            });
            return response;
        },
        async row_init(search_data) {
            return this.getlist(search_data).then(response => {
                console.log(response);
                // 초기화
                this.virtualAccountObj.success_amt = 0;
                this.virtualAccountObj.danger_amt = 0;
                this.virtualAccountObj.error_amt = 0;
                if (response.data.code === 200) {
                    this.virtualAccountObj.data = response.data.data;

                    // 각 아이템에 대해 결과 메시지(result_msg) 계산
                    this.virtualAccountObj.data.forEach(item => {
                        let result_msg = '';
                        if (item.RESULTCD == null) {
                            result_msg = '등록대기';
                        } else if (item.RESULTCD != "D000") {
                            result_msg = `등록실패[${item.RESULTCD}]<br/>${item.RESULTMSG}`;
                            this.virtualAccountObj.error_amt += parseInt(item.PAYITEMAMT);
                        } else {
                            result_msg = '미납';
                            if (item.REGDT !== null) {
                                this.virtualAccountObj.success_amt += parseInt(item.PAYITEMAMT);
                                result_msg = `납부<br/> ${item.PAYDAY.replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1-$2-$3 $4:$5:$6')}`;
                            } else {
                                this.virtualAccountObj.danger_amt += parseInt(item.PAYITEMAMT);
                            }
                        }
                        // 각 항목에 result_msg 속성 추가
                        item.result_msg = result_msg;
                    });
                } else {
                    this.virtualAccountObj.data = [];
                }
            });
        },
        async getacc() {
            const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/virtualaccount/getacc`, {
                thebill_id: this.$globalState.thebill_id,
                company: this.$globalState.company
            });
            this.virtualAccountObj.use_list = response.data.data.use_list;
            this.virtualAccountObj.notuse_list = response.data.data.notuse_list;
            return response;
        }
    },

    created() {
        this.restoreState();
        if (!this.virtualSDate) this.virtualSDate = new Date(1999, 0, 1);
        if (!this.virtualEDate) this.virtualEDate = new Date(2099, 0, 1);
        if (!this.virtualStatus) this.virtualStatus = "전체";
        this.loadVirtualAccounts();
    },

    // 컴포넌트가 활성화될 때 세션 스토리지 설정
    activated() {
        sessionStorage.setItem('accountPageVisited', 'true');
    },

    // 컴포넌트가 비활성화될 때 상태 저장
    deactivated() {
        this.saveState();
    },

    // 컴포넌트가 파괴될 때 상태 저장
    beforeUnmount() {
        this.saveState();
        // 페이지를 떠날 때 세션 스토리지 제거
        sessionStorage.removeItem('accountPageVisited');
    },

    // 모달 상태 변경 감시
    watch: {
        showModal_obj: {
            handler() {
                this.saveState();
            },
            deep: true
        },

        // 검색 조건 변경 감시
        virtualStatus() {
            this.saveState();
        },

        virtualSearch() {
            this.saveState();
        }
    },
}
</script>

<style scoped>
.pay {
    background-color: #b0fcb0 !important;
}

.non-pay {
    background-color: #ffa19b !important;
}

.error {
    background-color: #ffeccc !important;
}

/* 블러 효과를 위한 CSS */
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>
